import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";

// Admin-only: Kitchen shortage found at pickup. Admin picks the billable qty per line before the invoice can be issued.
export const ShortageReview = ({ inv, onResolved }) => {
  const review = inv?.shortage_review;
  const [qty, setQty] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (!review) return null;
  const pending = review.status === "pending";
  const lines = review.lines || [];
  const ready = lines.every((l) => qty[l.item_id] !== undefined && qty[l.item_id] !== "");

  const submit = async () => {
    setBusy(true);
    try {
      const billable_quantities = Object.fromEntries(lines.map((l) => [l.item_id, Number(qty[l.item_id])]));
      await api.post(`/invoices/${inv.id}/shortage-review`, { billable_quantities, note: note || undefined });
      toast.success("Shortage resolved · invoice quantities updated");
      onResolved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="shortage-review-panel"
      className={`max-w-3xl mx-auto mb-6 rounded-xl border p-5 no-print ${pending ? "border-[#B7791F]/40 bg-[#FFF8EB]" : "border-border bg-card"}`}>
      <div className="flex items-center gap-2 text-sm font-medium mb-1">
        {pending ? <AlertTriangle className="h-4 w-4 text-[#B7791F]" /> : <CheckCircle2 className="h-4 w-4 text-[#2E7D32]" />}
        <span data-testid="shortage-review-status">
          {pending ? "Kitchen stock short at pickup — choose billable quantities before sending"
                   : `Shortage resolved by ${review.resolved_by} · ${new Date(review.resolved_at).toLocaleString()}`}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Franchisee inventory received only the transferred amount. Billing is your decision; stock is not changed here.
      </p>
      <table className="w-full text-xs">
        <thead><tr className="text-muted-foreground text-left">
          <th className="py-1">Item</th><th className="text-right">Ordered</th><th className="text-right">Approved</th>
          <th className="text-right">Transferred</th><th className="text-right">Short</th><th className="text-right">Billable</th>
        </tr></thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.item_id} className="border-t border-border" data-testid={`shortage-line-${l.item_id}`}>
              <td className="py-2 font-medium">{l.name}</td>
              <td className="text-right tabular-nums">{l.ordered}</td>
              <td className="text-right tabular-nums">{l.fulfilled}</td>
              <td className="text-right tabular-nums">{l.transferred}</td>
              <td className="text-right tabular-nums text-[#B7791F]">{Math.round((l.fulfilled - l.transferred) * 100) / 100}</td>
              <td className="text-right">
                {pending ? (
                  <input type="number" min={0} max={l.ordered} value={qty[l.item_id] ?? ""} placeholder="—"
                    onWheel={(e) => e.currentTarget.blur()}
                    onChange={(e) => setQty((q) => ({ ...q, [l.item_id]: e.target.value === "" ? "" : Math.max(0, Math.min(l.ordered, Number(e.target.value))) }))}
                    data-testid={`shortage-billable-input-${l.item_id}`}
                    className="w-16 h-8 px-2 rounded-md border border-border bg-background text-right tabular-nums" />
                ) : <span className="tabular-nums font-medium">{l.billable}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {pending ? (
        <div className="mt-3 flex items-center gap-2">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)"
            data-testid="shortage-review-note" className="flex-1 h-9 px-3 rounded-md border border-border bg-background text-xs" />
          <button onClick={submit} disabled={!ready || busy} data-testid="shortage-review-resolve-btn"
            className="h-9 px-4 rounded-md text-xs font-medium bg-foreground text-background hover:bg-foreground/90 disabled:opacity-50">
            {busy ? "Saving…" : "Confirm billable quantities"}
          </button>
        </div>
      ) : review.note && <p className="mt-2 text-xs italic text-muted-foreground">Note: {review.note}</p>}
    </div>
  );
};
